import React, { useMemo, useState } from 'react'
import {
  DEFAULT_EMAIL_FAX_TOOLBAR,
  DEFAULT_SMS_NOTES_TOOLBAR_WITH_VARIABLES,
  RichTextEditor,
  RICH_TEXT_EDITOR_VARIANT_LABELS,
  RICH_TEXT_EDITOR_VARIANTS,
  RICH_TEXT_EDITOR_AI_ACTION_LABELS,
  RICH_TEXT_EDITOR_AI_TONE_LABELS,
  type RichTextEditorAiActionId,
  type RichTextEditorAiToneId,
  type RichTextEditorBodyActionId,
  type RichTextEditorFooterActionId,
  type RichTextEditorToolbarGroup,
  type RichTextEditorToolbarItemId,
  type RichTextEditorVariable,
  type RichTextEditorVariant,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './RichTextEditorShowcase.css'

const MINIMAL_TOOLBAR: RichTextEditorToolbarGroup[] = [
  ['bold', 'italic', 'underline', 'strikethrough', 'bulletList', 'link'],
]

const SAMPLE_VARIABLES: RichTextEditorVariable[] = [
  {
    id: 'patient-first-name',
    label: 'Patient first name',
    value: '{{patient.firstName}}',
  },
  {
    id: 'patient-last-name',
    label: 'Patient last name',
    value: '{{patient.lastName}}',
  },
  {
    id: 'appointment-date',
    label: 'Appointment date',
    value: '{{appointment.date}}',
  },
  { id: 'clinic-name', label: 'Clinic name', value: '{{clinic.name}}' },
]

const RichTextEditorShowcase: React.FC = () => {
  const [variant, setVariant] = useState<RichTextEditorVariant>('email-fax')
  const [value, setValue] = useState('')
  const [plainText, setPlainText] = useState('')
  const [lastVariable, setLastVariable] = useState<string | null>(null)
  const [disabled, setDisabled] = useState(false)
  const [useMinimalToolbar, setUseMinimalToolbar] = useState(false)
  const [toolbarOnly, setToolbarOnly] = useState(false)
  const [toolbarActions, setToolbarActions] = useState(true)
  const [lastToolbarAction, setLastToolbarAction] =
    useState<RichTextEditorToolbarItemId | null>(null)
  const [lastBodyAction, setLastBodyAction] =
    useState<RichTextEditorBodyActionId | null>(null)
  const [lastFooterAction, setLastFooterAction] =
    useState<RichTextEditorFooterActionId | null>(null)
  const [aiEnabled, setAiEnabled] = useState(true)
  const [aiResult, setAiResult] = useState<string | null>(null)
  const [attachedNames, setAttachedNames] = useState<string[]>([])

  const toolbarGroups = useMinimalToolbar
    ? MINIMAL_TOOLBAR
    : variant === 'email-fax'
      ? DEFAULT_EMAIL_FAX_TOOLBAR
      : DEFAULT_SMS_NOTES_TOOLBAR_WITH_VARIABLES

  const handleAiAction = (actionId: RichTextEditorAiActionId): void => {
    setAiResult(
      `${RICH_TEXT_EDITOR_AI_ACTION_LABELS[actionId]}: There are still a few forms to be completed.`,
    )
  }

  const handleAiToneSelect = (toneId: RichTextEditorAiToneId): void => {
    setAiResult(
      `${RICH_TEXT_EDITOR_AI_TONE_LABELS[toneId]} tone: There are still a few forms to be completed.`,
    )
  }

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'rte-variant',
            label: 'Variant',
            control: (
              <select
                id="rte-variant"
                className="ui-field-control"
                value={variant}
                onChange={(event) =>
                  setVariant(event.target.value as RichTextEditorVariant)
                }
              >
                {RICH_TEXT_EDITOR_VARIANTS.map((option) => (
                  <option key={option} value={option}>
                    {RICH_TEXT_EDITOR_VARIANT_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'rte-minimal-toolbar',
            label: 'Toolbar preset',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="rte-minimal-toolbar"
                  type="checkbox"
                  checked={useMinimalToolbar}
                  onChange={(event) =>
                    setUseMinimalToolbar(event.target.checked)
                  }
                />
                <span>Use minimal toolbar</span>
              </label>
            ),
          },
          {
            id: 'rte-toolbar-only',
            label: 'Toolbar only',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="rte-toolbar-only"
                  type="checkbox"
                  checked={toolbarOnly}
                  onChange={(event) => setToolbarOnly(event.target.checked)}
                />
                <span>Hide body and footer</span>
              </label>
            ),
          },
          {
            id: 'rte-toolbar-actions',
            label: 'Inline actions',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="rte-toolbar-actions"
                  type="checkbox"
                  checked={toolbarActions}
                  disabled={!toolbarOnly}
                  onChange={(event) => setToolbarActions(event.target.checked)}
                />
                <span>Bar surface with actions</span>
              </label>
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'rte-disabled',
            label: 'Disabled',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="rte-disabled"
                  type="checkbox"
                  checked={disabled}
                  onChange={(event) => setDisabled(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'rte-ai',
            label: 'AI assist',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="rte-ai"
                  type="checkbox"
                  checked={aiEnabled}
                  onChange={(event) => {
                    setAiEnabled(event.target.checked)
                    setAiResult(null)
                  }}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [
      aiEnabled,
      disabled,
      toolbarActions,
      toolbarOnly,
      useMinimalToolbar,
      variant,
    ],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Rich text editor"
        description="Message editor with formatting toolbar and contextual actions for Email/Fax and SMS/Notes workflows."
        stageClassName={`ui-preview__stage--wide rich-text-editor-showcase__stage-wrap`}
      >
        <div className={`rich-text-editor-showcase__sheet`}>
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Select text and use the toolbar to apply formatting. With AI
              assist on, selecting text opens the AI menu; picking an action
              swaps it for the result menu. Custom actions (variable, AI, chips,
              footer icons) report the last action below.
            </p>
            <RichTextEditor
              variant={variant}
              value={value}
              disabled={disabled}
              toolbarGroups={toolbarGroups}
              toolbarOnly={toolbarOnly}
              toolbarActions={toolbarActions}
              aiEnabled={aiEnabled}
              aiResult={aiResult}
              variables={SAMPLE_VARIABLES}
              onChange={(html, text) => {
                setValue(html)
                setPlainText(text)
              }}
              onVariableSelect={(variable) => setLastVariable(variable.label)}
              onToolbarAction={setLastToolbarAction}
              onBodyAction={setLastBodyAction}
              onFooterAction={setLastFooterAction}
              onSend={() => setLastFooterAction('send')}
              onAttachmentsChange={(files) =>
                setAttachedNames(files.map((file) => file.name))
              }
              onAiAction={handleAiAction}
              onAiToneSelect={handleAiToneSelect}
              onAiResultAction={(actionId, result) => {
                if (actionId === 'regenerate') {
                  setAiResult(`${result} (regenerated)`)
                  return
                }

                if (actionId === 'replace' || actionId === 'insert') {
                  setValue(result)
                }

                if (actionId !== 'copy') {
                  setAiResult(null)
                }
              }}
            />
            {lastToolbarAction ||
            lastBodyAction ||
            lastFooterAction ||
            lastVariable ||
            plainText ||
            attachedNames.length > 0 ? (
              <div className="rich-text-editor-showcase__feedback">
                {lastToolbarAction ? (
                  <p className="rich-text-editor-showcase__feedback-line">
                    Last toolbar action: {lastToolbarAction}
                  </p>
                ) : null}
                {lastBodyAction ? (
                  <p className="rich-text-editor-showcase__feedback-line">
                    Last body action: {lastBodyAction}
                  </p>
                ) : null}
                {lastFooterAction ? (
                  <p className="rich-text-editor-showcase__feedback-line">
                    Last footer action: {lastFooterAction}
                  </p>
                ) : null}
                {lastVariable ? (
                  <p className="rich-text-editor-showcase__feedback-line">
                    Last variable: {lastVariable}
                  </p>
                ) : null}
                {plainText ? (
                  <p className="rich-text-editor-showcase__feedback-line">
                    Plain text: {plainText}
                  </p>
                ) : null}
                {attachedNames.length > 0 ? (
                  <p className="rich-text-editor-showcase__feedback-line">
                    Attached: {attachedNames.join(', ')}
                  </p>
                ) : null}
              </div>
            ) : null}
          </section>

          <section className="rich-text-editor-showcase__matrix">
            {RICH_TEXT_EDITOR_VARIANTS.map((typeVariant) => (
              <div key={typeVariant} className="rich-text-editor-showcase__row">
                <h4 className="rich-text-editor-showcase__label">
                  {RICH_TEXT_EDITOR_VARIANT_LABELS[typeVariant]}
                </h4>
                <RichTextEditor variant={typeVariant} />
              </div>
            ))}
            <div className="rich-text-editor-showcase__row">
              <h4 className="rich-text-editor-showcase__label">
                Email &amp; Fax &amp; Notes (Toolbar only)
              </h4>
              <RichTextEditor variant="email-fax" toolbarOnly toolbarActions />
            </div>
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel
        componentName="Rich text editor"
        sections={propertySections}
      />
    </>
  )
}

export default RichTextEditorShowcase
